// -----------------------------------------------------------------------------
// Science Sorted - site content.
//
// IMPORTANT: Every value flagged with `placeholder: true` (or noted in a
// comment as "PLACEHOLDER") is a stand-in for real Science Sorted content and
// MUST be replaced before launch. Do NOT publish invented tutor names,
// qualifications, prices, statistics or reviews. Suggested owner: marketing.
// -----------------------------------------------------------------------------

export const brand = {
  name: 'Science Sorted',
  tagline: 'Teacher and Examiner-Led Tuition',
  email: 'info@sciencesorted.co.uk',
  phone: '+44 7480 066499',
  phoneDisplay: '+44 7480 066499',
  whatsappNumber: '447480066499', // digits only, no +
  whatsappUrl: 'https://wa.me/447480066499?text=Hi%20Science%20Sorted%2C%20I%27d%20like%20to%20book%20a%20free%20trial.',
  logoIcon:
    'https://customer-assets-39nsmqrw.emergentagent.net/job_science-tuition-hub-1/artifacts/m7fgqlzx_Science%20Sorted%20Logo.ai%20%285%29.png',
  logoUrl:
    'https://customer-assets-39nsmqrw.emergentagent.net/job_science-tuition-hub-1/artifacts/whdheekj_Science%20Sorted%20Logo-03.png',
};

// Unified CTA labels used across the entire site.
export const CTA = {
  primary: 'Book a free trial',
  secondary: 'View courses',
};

// Compact trust/results strip near hero. PLACEHOLDER values.
export const heroTrust = {
  placeholder: true,
  rating: '4.9 / 5', // PLACEHOLDER - pull from live Trustpilot
  reviewsLabel: 'Rated by parents & students',
  bullets: [
    'Qualified UK teachers',
    'Current examiners',
    'AQA · OCR · Edexcel',
  ],
};

export const pillars = [
  {
    id: 'p1',
    title: 'Qualified teachers',
    body: 'Live lessons taught by qualified UK science teachers with strong classroom and examining experience.',
    icon: 'GraduationCap',
  },
  {
    id: 'p2',
    title: 'Structured by specification',
    body: 'A carefully sequenced curriculum aligned to AQA and OCR, so students learn each topic in the right order.',
    icon: 'BookOpenCheck',
  },
  {
    id: 'p3',
    title: 'Exam technique and feedback',
    body: 'Students practise high-value exam questions and receive clear feedback on how to gain more marks.',
    icon: 'Sparkles',
  },
];

// Courses - three top-level courses shown on the homepage and courses page.
// Each course covers both AQA and OCR. Detail pages live at /courses/:id.
export const courses = [
  {
    id: 'gcse-science',
    level: 'GCSE',
    yearGroup: 'Years 9–11',
    title: 'GCSE Science',
    subtitle: 'Biology · Chemistry · Physics',
    boards: ['AQA', 'OCR'],
    tags: ['AQA', 'OCR', 'Years 9–11'],
    body: 'Structured tuition across Biology, Chemistry and Physics, with a strong focus on exam technique.',
    exploreLabel: 'View GCSE course',
    image: '/hero/course-gcse.png',
    lessonFrequency: 'Weekly · 1 hour per subject',
    groupSize: null,
    price: {
      headline: '£13/hour',
      subline: 'if all three sciences are taken, otherwise £15/hour',
    },
    highlights: [
      'Live weekly one-hour lessons',
      'Exam-question practice',
      'Homework between lessons',
      'Regular assessments',
      'Weekly examiner and teacher feedback',
      'Access to teacher-produced flashcards',
      '24/7 contact with your teacher',
    ],
  },
  {
    id: 'a-level-biology',
    level: 'A-LEVEL',
    yearGroup: 'Years 12–13',
    title: 'A Level Biology',
    subtitle: 'AQA & OCR A specifications',
    boards: ['AQA', 'OCR A'],
    tags: ['AQA', 'OCR A', 'Years 12–13'],
    body: 'Develop deeper subject knowledge, practical understanding and the exam technique needed for higher grades.',
    exploreLabel: 'View Biology course',
    image: '/hero/course-biology.png',
    lessonFrequency: 'Weekly · 1 hour',
    groupSize: null,
    price: {
      headline: '£19/hour',
      subline: null,
    },
    highlights: [
      'Live weekly one-hour lessons',
      'Specification-by-specification teaching',
      'Required practicals',
      'Synoptic questions',
      'Exam technique',
      'Regular assessments',
      'Weekly examiner and teacher feedback',
      'Access to teacher-produced flashcards',
      '24/7 contact with your teacher',
    ],
  },
];

// Service tiers - body copy kept generic; PRICES ARE PLACEHOLDERS.
export const services = [
  {
    id: 's1',
    title: 'Group-based online GCSE Science tuition',
    body: 'Small online groups covering the full GCSE Science specification.',
    perks: ['Small groups (max 6)', 'Weekly 90-min lessons', 'Session recordings'],
    price: 'Pricing available on request', // PLACEHOLDER
  },
  {
    id: 's2',
    title: '1:1 online GCSE Science tuition',
    body: 'One-to-one lessons tailored to your child’s needs and target grade.',
    perks: ['Fully personalised', 'Flexible timing', 'Homework feedback'],
    price: 'Pricing available on request', // PLACEHOLDER
  },
  {
    id: 's3',
    title: 'Group-based online A-level tuition',
    body: 'Small-group A-level Biology and Chemistry courses.',
    perks: ['AQA & OCR', '2-year programmes', 'Weekly assessments'],
    price: 'Pricing available on request', // PLACEHOLDER
  },
  {
    id: 's4',
    title: '1:1 online A-level tuition',
    body: 'Bespoke A-level Biology or Chemistry tutoring with a subject specialist.',
    perks: ['Full syllabus', 'Exam technique focus', 'Progress reviews'],
    price: 'Pricing available on request', // PLACEHOLDER
  },
];

// Results shown on the homepage "Results" band. Update these figures once new
// exam-cycle data is available.
export const results = {
  eyebrow: 'Results',
  heading: 'Results that speak for themselves.',
  intro: 'Based on our 2025/26 student cohort.',
  comparisons: [
    {
      id: 'alevel-biology',
      label: 'A Level Biology',
      metric: 'A* / A',
      us: 68.3,
      national: 28,
    },
    {
      id: 'gcse-science',
      label: 'GCSE Science',
      metric: 'Grades 7–9',
      us: 78.4,
      national: 45.8,
    },
  ],
  gradeJump: {
    value: 2,
    label: 'grades',
    suffix: 'Students improve by an average of two grades.',
  },
  footnote: 'National figures are the most recent Ofqual/JCQ published averages.',
};

// FAQs - genuine Science Sorted answers.
export const faqs = [
  {
    q: 'What makes Science Sorted different from other tuition providers?',
    a: "Our lessons are taught by qualified UK teachers and follow a carefully structured curriculum. Science Sorted is founded by a teacher with over nine years' UK classroom experience and current examining experience with both AQA and OCR. Lessons focus on strong subject knowledge, exam technique, regular practice and clear feedback.",
  },
  {
    q: 'Which exam boards and courses do you cover?',
    a: 'We specialise in AQA and OCR. We offer GCSE Science and A Level Biology tuition. For A Level courses, we currently teach the AQA and OCR A specifications.',
  },
  {
    q: 'Are lessons online or in person?',
    a: 'All lessons are delivered live online, allowing students to learn from experienced UK teachers from home. Lessons are interactive, structured and include opportunities to ask questions and practise exam skills.',
  },
  {
    q: 'How much do lessons cost?',
    a: 'Each lesson is one hour long. A Level lessons cost £19 per session. GCSE Science lessons cost £15 per subject, per session when taking one or two sciences, or £13 per subject, per session when enrolled in all three sciences: Biology, Chemistry and Physics. The first trial lesson is free, with no obligation to continue.',
  },
  {
    q: 'Can my child try a lesson before joining?',
    a: 'Absolutely! We offer a free trial lesson so your child can experience the teaching, meet the teacher and decide whether the course is right for them. There is no obligation to continue afterwards.',
  },
  {
    q: 'Is homework provided?',
    a: 'Yes. Students receive targeted homework and exam-question practice to reinforce lesson content. Work is reviewed regularly so that students understand how to improve their answers. Each student receives weekly teacher & examiner feedback.',
  },
  {
    q: 'How do you track student progress?',
    a: "Progress is monitored through regular assessments, homework and lesson performance. Parents receive clear updates about their child's strengths, areas for improvement and progress over time.",
  },
];

// Tutors - real team members. Replace `image`, `photoScale`,
// `photoOrigin` and `photoPosition` if a new photo is provided.
export const tutors = [
  {
    id: 'qasid',
    name: 'Qasid Ahmed',
    role: 'Founder & Lead Biology Tutor',
    bio: "Qualified UK science teacher with over nine years' experience teaching in UK schools. Qasid completed his PGCE at King's College London and is a current examiner for both AQA and OCR.",
    bioExtra:
      'He specialises in GCSE Science and A Level Biology, with a particular focus on helping students develop strong subject knowledge and understand exactly what exam questions and mark schemes require.',
    credentials: [
      '9+ years teaching',
      'PGCE, King\u2019s College London',
      'AQA Examiner',
      'OCR Examiner',
    ],
    subjects: ['GCSE Science', 'A Level Biology'],
    image: '/team/qasid-ahmed.png',
    // Precise crop via background-image. Centres the face and keeps the top
    // of the tie visible; the flower has already been removed from the file.
    photoStyle: {
      backgroundSize: '145% auto',
      backgroundPosition: '30% 12%',
    },
  },
];

export const howItWorks = [
  {
    step: '01',
    title: 'Book a free trial',
    body: 'Get in touch and we\u2019ll arrange a free trial lesson for the appropriate subject, year group and exam board.',
  },
  {
    step: '02',
    title: 'Attend your trial lesson',
    body: 'Experience a live Science Sorted lesson and see whether the course is right for your child, with no obligation to continue.',
  },
  {
    step: '03',
    title: 'Start learning',
    body: 'Join weekly live lessons, complete targeted homework and practise high-value exam questions with regular teacher feedback.',
  },
  {
    step: '04',
    title: 'Track progress',
    body: 'Regular assessments and clear updates help you understand your child\u2019s strengths, areas for improvement and progress over time.',
  },
];

// What's included in every Science Sorted course - shown on /courses.
export const courseIncludes = [
  {
    id: 'ci-live',
    title: 'Live weekly teaching',
    body: 'One-hour lessons with a qualified UK teacher.',
    icon: 'Video',
  },
  {
    id: 'ci-exam',
    title: 'Exam-focused practice',
    body: 'Regular questions based on AQA and OCR specifications.',
    icon: 'ClipboardCheck',
  },
  {
    id: 'ci-homework',
    title: 'Homework',
    body: 'Targeted work between lessons.',
    icon: 'BookOpenCheck',
  },
  {
    id: 'ci-progress',
    title: 'Progress tracking',
    body: 'Regular assessments and feedback.',
    icon: 'LineChart',
  },
  {
    id: 'ci-resources',
    title: 'Resources',
    body: 'Access to lesson materials and revision support.',
    icon: 'FolderOpen',
  },
  {
    id: 'ci-trial',
    title: 'Free trial lesson',
    body: 'Experience the course before committing.',
    icon: 'Sparkles',
  },
];


// Trustpilot integration - PLACEHOLDER data. When ready, embed the official
// Trustpilot widget (https://businessapp.b2b.trustpilot.com/) or fetch via API.
export const trustpilot = {
  placeholder: true,
  url: 'https://uk.trustpilot.com/review/sciencesorted.co.uk',
  rating: null, // PLACEHOLDER - real live value
  totalReviews: null, // PLACEHOLDER
  label: 'See our reviews',
  reviews: [
    // These are PLACEHOLDER cards. They must not be published as if they were
    // real customer reviews. Recommended path: replace this array with data
    // from the Trustpilot business API, or embed their widget.
    { id: 'tp-p1', placeholder: true, name: 'Verified reviewer', title: 'Placeholder review', rating: 5, date: 'Sample', body: 'Real Trustpilot review text will appear here once the Trustpilot widget or API integration is added.' },
    { id: 'tp-p2', placeholder: true, name: 'Verified reviewer', title: 'Placeholder review', rating: 5, date: 'Sample', body: 'Real Trustpilot review text will appear here once the Trustpilot widget or API integration is added.' },
    { id: 'tp-p3', placeholder: true, name: 'Verified reviewer', title: 'Placeholder review', rating: 5, date: 'Sample', body: 'Real Trustpilot review text will appear here once the Trustpilot widget or API integration is added.' },
    { id: 'tp-p4', placeholder: true, name: 'Verified reviewer', title: 'Placeholder review', rating: 5, date: 'Sample', body: 'Real Trustpilot review text will appear here once the Trustpilot widget or API integration is added.' },
  ],
};
